import { CallLogListItem } from 'Components/CallLogs/CallLogListItem';
import { CALL_TYPES_CONTENT } from 'Components/CallLogs/constants';
import { OfflineBanner } from 'Components/CallLogs/OfflineBanner';
import LazyLoader from 'Components/LazyLoader';
import { ContentArea } from 'Components/shared/ContentArea';
import { Heading } from 'Components/shared/Heading';
import { CALL_LOGS_DELETION } from 'Constants/featureFlags';
import { TESTID_CALL_LOGS } from 'Constants/testids';
import useFeatureFlags from 'Hooks/featureFlags/useFeatureFlags';
import { observer } from 'mobx-react';
import { createContactFilterOption } from 'Modules/conversation/createConversation/components/createConversationForm/resolver';
import React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { formatDateGroup } from 'Utils/formatDateGroup';
import { Autocomplete } from '~/lib/ui/autocomplete';
import { Button } from '~/lib/ui/button';
import { CallLogCategoryType } from '../types';
import { useCallLogsBulkDelete } from './hooks/useCallLogsBulkDelete';
import { useCallLogsSearch } from './hooks/useCallLogsSearch';
import { getCallLogRowLayout } from './utils/getCallLogRowLayout';

interface ICallsDirectoryListProps {
  typeOfCallId: CallLogCategoryType;
}

const SEARCH_DISABLED_WHILE_SELECTING =
  'Search call records, disabled while selection mode is active.';

export const CallsDirectoryList: React.FC<ICallsDirectoryListProps> = observer(
  ({ typeOfCallId }) => {
    const {
      allLogs,
      allContactOptions,
      selectedOption,
      isLoading,
      contactsLoading,
      isFetchingNextContactsPage,
      searchQuery,
      hasMoreCallLogs,
      handleChange,
      handleLoadCallLogs,
      loadMoreContacts,
      setInputValue,
    } = useCallLogsSearch(typeOfCallId);

    const { [CALL_LOGS_DELETION]: isCallLogsDeletionEnabled } =
      useFeatureFlags([CALL_LOGS_DELETION]);

    const {
      selectedIds,
      selectedCount,
      isSelecting,
      isMaxReached,
      isDeleting,
      startSelecting,
      cancelSelecting,
      toggleSelect,
      selectAllVisible,
      handleRequestDelete,
    } = useCallLogsBulkDelete(typeOfCallId);

    const isSelectionMode =
      Boolean(isCallLogsDeletionEnabled) && isSelecting;

    const isAllLoadedSelected =
      Boolean(allLogs?.length) &&
      allLogs.every((log) => selectedIds.has(log.id));

    const isSelectAllDisabled =
      isMaxReached || (isAllLoadedSelected && !hasMoreCallLogs);

    const showBulkDeleteFooter =
      isSelectionMode && Boolean(allLogs?.length);

    const renderEmptyContent = () => {
      const isSearching = searchQuery.trim().length > 2;

      if (!isLoading && !isSearching && typeOfCallId === 'all') {
        return (
          <div className="flex-1 min-h-0 flex items-center justify-center">
            {CALL_TYPES_CONTENT.all.emptySearchText}
          </div>
        );
      }

      return (
        <div className="max-w-[618px] w-[70%] mt-[50px] mb-[10px] mr-[3%] ml-[27px] text-center text-secondary text-[16px] leading-[26px] shrink-0">
          {isLoading ? (
            <LazyLoader />
          ) : isSearching ? (
            <>
              <div>Oh no! We couldn&apos;t find what you were looking for.</div>
              <div>Check your spelling or try searching for other terms.</div>
            </>
          ) : (
            CALL_TYPES_CONTENT[typeOfCallId].emptySearchText
          )}
        </div>
      );
    };

    return (
      <ContentArea id="call-logs" inlineTopBanner={<OfflineBanner />}>
        <Heading variant="h2" className="!mt-[45px] shrink-0">
          {CALL_TYPES_CONTENT[typeOfCallId].title}
        </Heading>
        <div className="shrink-0">
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="sr-only"
          >
            {isSelectionMode ? SEARCH_DISABLED_WHILE_SELECTING : ''}
          </div>
          <Autocomplete
            options={allContactOptions}
            filterOption={createContactFilterOption}
            value={selectedOption}
            onChange={handleChange}
            onInputChange={setInputValue}
            onMenuScrollToBottom={loadMoreContacts}
            isLoading={contactsLoading || isFetchingNextContactsPage}
            placeholder="Type something"
            isClearable={false}
            focusedPlaceholder="Start a new search"
            testID={TESTID_CALL_LOGS['DIRECTORY_LIST']}
            isDisabled={isSelectionMode}
          />
        </div>
        {isCallLogsDeletionEnabled && !!allLogs?.length && (
          <div className="flex justify-end items-center gap-[12px] mt-[20px] text-[13px] shrink-0">
            {isSelecting ? (
              <>
                {selectedCount > 0 && (
                  <span>{selectedCount} selected</span>
                )}
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0"
                  onClick={() => selectAllVisible(allLogs)}
                  disabled={isSelectAllDisabled}
                >
                  Select all
                </Button>
                {selectedCount > 0 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0"
                    onClick={cancelSelecting}
                  >
                    Deselect all
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0"
                onClick={startSelecting}
              >
                Select
              </Button>
            )}
          </div>
        )}
        {allLogs?.length ? (
          <div
            id="directory-list"
            className="mt-[15px] min-h-0 flex-1 overflow-hidden hover:overflow-y-auto scroll-area-content [scrollbar-gutter:stable]"
          >
            <InfiniteScroll
              initialScrollY={1}
              dataLength={allLogs.length}
              next={handleLoadCallLogs}
              hasMore
              loader={<></>}
              endMessage={
                <p className="text-center">
                  <b>You have seen it all</b>
                </p>
              }
              refreshFunction={handleLoadCallLogs}
              pullDownToRefresh
              pullDownToRefreshThreshold={4}
              scrollableTarget="directory-list"
            >
              {allLogs.map((callLog, index) => {
                const {
                  showDateSeparator,
                  removeTopPadding,
                  removeBottomPadding,
                  showTopBorder,
                } = getCallLogRowLayout(allLogs, index);
                const dateGroup = showDateSeparator
                  ? formatDateGroup(callLog.startedAt)
                  : null;

                return (
                  <React.Fragment key={`${callLog.id}-${callLog.direction}`}>
                    {showDateSeparator && dateGroup && (
                      <div className="mt-[30px] mb-[15px] text-[18px] leading-[26px] text-black h-[25px]">
                        {dateGroup}
                      </div>
                    )}
                    <CallLogListItem
                      {...callLog}
                      removeTopPadding={removeTopPadding}
                      removeBottomPadding={removeBottomPadding}
                      showTopBorder={showTopBorder}
                      selected={selectedIds.has(callLog.id)}
                      onToggleSelect={toggleSelect}
                      isSelectionMode={isSelectionMode}
                    />
                  </React.Fragment>
                );
              })}
            </InfiniteScroll>
          </div>
        ) : (
          renderEmptyContent()
        )}
        {!!allLogs?.length && (
          <div className="box-border flex h-[60px] shrink-0 items-center justify-end gap-[12px] pt-[15px]">
            {showBulkDeleteFooter && (
              <>
                <Button
                  variant="outline"
                  onClick={cancelSelecting}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleRequestDelete}
                  disabled={selectedCount === 0 || isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
              </>
            )}
          </div>
        )}
      </ContentArea>
    );
  }
);
